import { getHeadingsFromMarkdown, getLowestHeadingLevel } from '../../navigation/helpers/utils.js';
const DEFAULT_DESCRIPTION_ENTRY = {
    TITLE: 'Introduction',
    SLUG: 'introduction',
};
const getDefaultDescriptionEntry = (generateId, parentId, info) => {
    const id = generateId({
        type: 'text',
        depth: 1,
        slug: DEFAULT_DESCRIPTION_ENTRY.SLUG,
        parentId: parentId,
        info,
        value: DEFAULT_DESCRIPTION_ENTRY.TITLE,
    });
    const entry = {
        id,
        title: DEFAULT_DESCRIPTION_ENTRY.TITLE,
        type: 'text',
    };
    return entry;
};
/**
 * Creates a hierarchical navigation structure from markdown headings in an OpenAPI or AsyncAPI description.
 *
 * The function processes markdown headings to create a two-level navigation tree:
 * - Level 1: Main sections (based on the lowest heading level found)
 * - Level 2: Subsections (one level deeper than the main sections)
 *
 * If the description starts with content rather than a heading, an "Introduction" section
 * is automatically added as the first entry.
 *
 * @param description - The markdown description text to process
 * @param generateId - Function to generate unique IDs for headings
 * @param parentId - The ID of the parent entry
 * @param info - OpenAPI or AsyncAPI Info Object
 *
 * @returns Array of TraversedDescription entries with their hierarchy
 */
export const traverseDescription = ({ generateId, parentId, info, }) => {
    const description = info.description?.trim();
    if (!description) {
        // Return a single entry for the introduction section
        return [getDefaultDescriptionEntry(generateId, parentId, info)];
    }
    const headings = getHeadingsFromMarkdown(description);
    const lowestLevel = getLowestHeadingLevel(headings);
    const entries = [];
    let descriptionHeadingsEntry = null;
    let currentParent = null;
    // Add "Introduction" as the first heading
    if (!description.startsWith('#')) {
        const entry = getDefaultDescriptionEntry(generateId, parentId, info);
        entries.push(entry);
        descriptionHeadingsEntry = entry;
    }
    // Go through each heading
    for (const heading of headings) {
        // Skip if not a main or sub heading
        if (heading.depth !== lowestLevel && heading.depth !== lowestLevel + 1) {
            continue;
        }
        const entry = {
            id: generateId({
                type: 'text',
                depth: heading.depth,
                slug: heading.slug,
                parentId: parentId,
                info: info,
                value: heading.value,
            }),
            title: heading.value,
            type: 'text',
        };
        if (heading.depth === lowestLevel) {
            entry.children = [];
            // Add to description headings to the 'Introduction' entry
            if (descriptionHeadingsEntry) {
                if (!descriptionHeadingsEntry.children) {
                    descriptionHeadingsEntry.children = [];
                }
                descriptionHeadingsEntry.children.push(entry);
            }
            // If no 'Introduction' entry, add to entries
            else {
                entries.push(entry);
            }
            currentParent = entry;
        }
        else if (currentParent) {
            currentParent.children?.push(entry);
        }
    }
    return entries;
};
